import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TextField,
  Autocomplete,
  Checkbox,
  Chip,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
//IDOGAN;436653;21022025
export interface SelectOption {
  label: string;
  value: string;
}

interface ByzMuiMultiSelectProps {
  options: SelectOption[];
  label: string;
  value: SelectOption[];
  onChange: (newValue: SelectOption[]) => void;
  minWidth?: string | null; //CAKPISIZ;453093;06052025;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
}

const ByzMuiMultiSelect: React.FC<ByzMuiMultiSelectProps> = ({
  options,
  label,
  value,
  onChange,
  minWidth, //CAKPISIZ;453093;06052025;
  size = "small",
  sx,
}) => {
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleChipCount, setVisibleChipCount] = useState(value.length);

  useEffect(() => {
    if (!autocompleteRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    observer.observe(autocompleteRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const availableWidth = Math.max(containerWidth - 80, 0); // padding + icon
    let usedWidth = 0;
    let count = 0;

    const estimateChipWidth = (text: string) => {
      const base = size === "small" ? 44 : 52;
      const perChar = size === "small" ? 6 : 7;
      return base + text.length * perChar;
    };

    for (const option of value) {
      const chipWidth = estimateChipWidth(option.label);
      if (usedWidth + chipWidth <= availableWidth) {
        usedWidth += chipWidth;
        count += 1;
      } else {
        break;
      }
    }

    if (count === 0 && value.length) {
      count = 1;
    }

    setVisibleChipCount(count);
  }, [containerWidth, value, size]);

  const hiddenChipTooltip = useMemo(
    () => value.map((item) => item.label).join(", "),
    [value]
  );

  const defaultSx: SxProps<Theme> = {
    "& .MuiInputLabel-root": {
      color: "#2498f3 !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2498f3 !important",
    },
    "& .MuiFormLabel-root": {
      color: "#2498f3 !important",
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#2498f3 !important",
    },
    "& .MuiOutlinedInput-root": {
      minHeight: size === "small" ? 38 : 48,
      padding: size === "small" ? "2px 10px" : "6px 12px",
    },
    "& .MuiAutocomplete-inputRoot": {
      padding: "0 !important",
      paddingLeft: "6px !important",
      alignItems: "center",
      flexWrap: "nowrap",
      minHeight: size === "small" ? 38 : 48,
      overflow: "hidden",
      gap: "2px",
      paddingRight: size === "small" ? "30px !important" : "36px !important",
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "6px 0 !important",
      minWidth: 0,
    },
    "& .MuiAutocomplete-tag": {
      margin: "0 2px 0 0",
      maxWidth: "100%",
    },
    "& .MuiChip-root": {
      display: "inline-flex",
      maxWidth: "100%",
      fontSize: 12,
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50%",
      transform: "translateY(-50%)",
    },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2498f3",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2498f3",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2498f3",
    },
  };

  const mergedSx = Array.isArray(sx)
    ? [defaultSx, ...sx]
    : sx
      ? [defaultSx, sx]
      : defaultSx;

  return (
    <Autocomplete
      ref={autocompleteRef}
      multiple
      options={options}
      value={value}
      onChange={(event, newValue) => onChange(newValue as SelectOption[])}
      disableCloseOnSelect
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
      size={size}
      sx={mergedSx}
      renderTags={(tagValue, getTagProps) => {
        if (!tagValue.length) return null;
        const visibleTags =
          visibleChipCount > 0 ? tagValue.slice(0, visibleChipCount) : [];
        const hiddenCount = tagValue.length - visibleTags.length;

        const chips = visibleTags.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.value}
            label={option.label}
            size="small"
          />
        ));

        if (hiddenCount > 0) {
          chips.push(
            <Tooltip key="hidden-summary" title={hiddenChipTooltip}>
              <Chip
                label={`+${hiddenCount}`}
                size="small"
                variant="outlined"
                sx={{ margin: 0 }}
              />
            </Tooltip>
          );
        }

        return chips;
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.value}>
          <Checkbox checked={selected} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          size={size}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: minWidth === null ? undefined : minWidth ?? "300px",
          }} //CAKPISIZ;453093;06052025;
        />
      )}
    />
  );
};

export default ByzMuiMultiSelect;
